import { imageDataToBase64 } from '@/common/images';
import { Camera } from '@/components/camera/component';
import { useFacility } from '@/contexts/facility-context/hook';
import { Flex, Loader, Text, Title } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

import { useBaleOutboundContext } from '../hook';
import styles from '../styles.module.css';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Header section with navigation and title
 */
const PageHeader: React.FC<{ onBack: () => void }> = ({ onBack }) => (
	<Flex
		className={styles.photoPageHeader}
		pos="fixed"
		top={0}
		left={0}
		px="md"
		py="lg"
		bg="white"
		w="100%"
		align="center"
		gap="sm"
	>
		<IconArrowLeft onClick={onBack} style={{ cursor: 'pointer' }} />
		<Title order={2}>Add Docket Photo</Title>
	</Flex>
);

/**
 * Loading overlay with smooth animations during photo processing
 */
const ProcessingOverlay: React.FC<{ isVisible: boolean }> = ({ isVisible }) => (
	<AnimatePresence>
		{isVisible && (
			<motion.div
				initial={{ opacity: 0 }}
				animate={{
					opacity: 1,
					transition: {
						duration: 0.4,
						ease: [0.25, 0.1, 0.25, 1],
					},
				}}
				exit={{
					opacity: 0,
					transition: {
						duration: 0.3,
						ease: [0.25, 0.1, 0.25, 1],
					},
				}}
				className={styles.loadingOverlay}
			>
				<Flex h="100%" w="100%" align="center" justify="center" direction="column" gap="md">
					{/* Animated Loader */}
					<motion.div
						initial={{
							opacity: 0,
							scale: 0.8,
							y: 20,
						}}
						animate={{
							opacity: 1,
							scale: 1,
							y: 0,
							transition: {
								duration: 0.6,
								delay: 0.2,
								ease: [0.25, 0.1, 0.25, 1],
								type: 'spring',
								damping: 20,
								stiffness: 300,
							},
						}}
						exit={{
							opacity: 0,
							scale: 0.8,
							y: -20,
							transition: {
								duration: 0.3,
								ease: [0.25, 0.1, 0.25, 1],
							},
						}}
					>
						<Loader color="blue" />
					</motion.div>

					{/* Loading Text */}
					<motion.div
						initial={{
							opacity: 0,
							y: 10,
						}}
						animate={{
							opacity: 1,
							y: 0,
							transition: {
								duration: 0.5,
								delay: 0.4,
								ease: [0.25, 0.1, 0.25, 1],
							},
						}}
						exit={{
							opacity: 0,
							y: -10,
							transition: {
								duration: 0.3,
								ease: [0.25, 0.1, 0.25, 1],
							},
						}}
					>
						<Text size="md" ta="center" c="white">
							Processing docket photo...
						</Text>
					</motion.div>
				</Flex>
			</motion.div>
		)}
	</AnimatePresence>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Page for capturing docket photos for outbound loads.
 *
 * This component provides a camera interface for taking docket photos,
 * which are required documentation for outbound bale loads. The photo
 * is processed and converted to base64 format before being stored.
 *
 * Features:
 * - Full-screen camera interface
 * - Animated loading state during photo processing
 * - Automatic navigation back to root page after photo capture
 * - High-quality JPEG compression (99%) for clear documentation
 */
export const BaleOutboundDocketAddPhotoPage = () => {
	// ========================================================================
	// CONTEXT AND STATE
	// ========================================================================

	const { setPage, handleInput } = useBaleOutboundContext();
	const { currentFacility } = useFacility();
	const [loading, setLoading] = useState(false);

	// ========================================================================
	// EVENT HANDLERS
	// ========================================================================

	/**
	 * Handle photo capture and processing
	 *
	 * Converts the captured image data to base64 format and saves it
	 * to the form data, then navigates back to the root page.
	 *
	 * @param imageData - The captured image data from the camera
	 */
	const handlePhotoAccepted = async (imageData: ImageData) => {
		// Ensure we have a valid facility before processing
		if (!currentFacility) {
			console.warn('No current facility available for docket photo');
			return;
		}

		setLoading(true);

		try {
			// Convert image data to base64 with high quality JPEG compression
			const photoDataUrl = `data:image/jpeg;base64,${imageDataToBase64(imageData, 'jpeg', 0.99)}`;

			// Save the photo to form data
			handleInput('docketPhoto', photoDataUrl);

			// Navigate back to the root page
			setPage('ROOT');
		} catch (error) {
			console.error('Error processing docket photo:', error);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Handle back navigation to root page
	 */
	const handleBack = () => {
		setPage('ROOT');
	};

	// ========================================================================
	// RENDER
	// ========================================================================

	return (
		<Flex className={`${styles.fullPageContainer} ${styles.pageBackground}`}>
			{/* Header */}
			<PageHeader onBack={handleBack} />

			{/* Camera Component */}
			<Camera onTakePhoto={handlePhotoAccepted} />

			{/* Loading Overlay */}
			<ProcessingOverlay isVisible={loading} />
		</Flex>
	);
};
